/** Согласование существительных с числом.
 *
 * В русском три формы, а не две, и правило не сводится к «дописать окончание»:
 * «вкус» действительно даёт «3 вкуса», но «сопротивление» так превращалось в
 * «3 сопротивлениеа». Отдельная беда — 11–14: они кончаются на 1–4, а ведут
 * себя как «много».
 *
 * Здесь два инструмента. `plural` — когда все три формы известны заранее и
 * написаны руками. `declineLabel` — когда слово приходит из настроек (подпись
 * выбора в разделе задаёт админ) и формы приходится выводить по последней
 * букве.
 */

/** Какая из трёх форм нужна числу: 0 — одна, 1 — две-четыре, 2 — много. */
function form(count: number): 0 | 1 | 2 {
  const hundreds = Math.abs(count) % 100;
  const ones = Math.abs(count) % 10;
  // 11–14 кончаются на 1–4, но ведут себя как «много»
  if (hundreds > 10 && hundreds < 20) return 2;
  if (ones === 1) return 0;
  if (ones >= 2 && ones <= 4) return 1;
  return 2;
}

/** «1 заявка», «2 заявки», «5 заявок». */
export function plural(count: number, one: string, few: string, many: string): string {
  return [one, few, many][form(count)] as string;
}

/** То же, но вместе с числом: `count(3, 'товар', 'товара', 'товаров')`. */
export function count(n: number, one: string, few: string, many: string): string {
  return `${n} ${plural(n, one, few, many)}`;
}

/**
 * Просклонять слово, которого нет в коде: подпись выбора («Вкус», «Вариант»,
 * «Сопротивление», «Крепость», «Доза») приходит из настроек раздела.
 *
 * Разбираем по последней букве. Это не полноценная морфология — она и не
 * нужна: на входе всегда существительное в единственном числе, названное
 * человеком, и таких форм ровно пять.
 */
export function declineLabel(label: string, n: number): string {
  const which = form(n);
  if (which === 0) return label;

  const stem = label.slice(0, -1);
  const last = label.slice(-1);

  if (last === 'ь') return which === 1 ? `${stem}и` : `${stem}ей`;
  if (last === 'я') return which === 1 ? `${stem}и` : `${stem}й`;
  if (last === 'а') return which === 1 ? `${stem}ы` : stem;
  if (last === 'е' || last === 'о') {
    const soft = label.endsWith('ие') || label.endsWith('ье');
    return which === 1 ? `${stem}${soft ? 'я' : 'а'}` : `${stem}${soft ? 'й' : ''}`;
  }
  // Согласный: у шипящих родительный падеж на «-ей», у остальных на «-ов»
  const hissing = 'жчшщц'.includes(last);
  return which === 1 ? `${label}а` : `${label}${hissing ? 'ей' : 'ов'}`;
}

/** То же вместе с числом: «3 сопротивления». */
export function countLabel(n: number, label: string): string {
  return `${n} ${declineLabel(label, n)}`;
}
